/**
 * Oracle Spine Editor — the editable prescribed-future spine (mobile).
 *
 * Per-sector spine view: Endeavour → Goal (deity) → Aim (actions).
 * Supports manual entry and survey-based intake. Edits produce a diff preview
 * before cascading changes to other systems.
 *
 * Mirrors web src/components/olmps/core/oracle-spine.tsx with mobile primitives.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ActivityIndicator, Pressable, ScrollView, StyleSheet, Switch, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { SECTOR_LABELS, type Sector, SECTORS, TOOL_NAMES } from '../domain/olmps';
import {
  useCompassConfig,
  builtinSpokes,
  RING_SYSTEMS,
  RING_SYSTEM_LABEL,
  type RingSystemId,
} from './sectorConfig';
import { SectorCompassPreview } from './SectorCompassPreview';
import { GlassCard, Atmos } from '../v10/ui';
import { useTheme, makeThemedStyles, DISPLAY_FONT, SECTOR_COLOR, radii } from '../v10/theme';
import type { ToolProps } from './ToolHost';
import {
  loadOracleSpine,
  saveOracleSpine,
  getOrCreateKyrsListForSector,
  insertAimTasks,
  delistAimTasks,
  buildDiffPreview,
  effectiveAims,
  getActivityPresetsForDeity,
  SECTOR_DEITY_MAP,
  PRESET_ENDEAVOURS,
  type OracleSpine,
  type OracleSpineNode,
  type OracleAim,
  type SpineDiffPreview,
} from './oracle-spine-data';
import { SECTOR_BANKS } from './question-banks';

/** Sector-to-deities mapping: map deity id from SECTOR_DEITY_MAP. */
function PageBg() {
  return (
    <View pointerEvents="none" style={StyleSheet.absoluteFill}>
      <Atmos />
    </View>
  );
}

function getSectorDeities(sector: Sector): string[] {
  const deities = SECTOR_DEITY_MAP[sector];
  return deities.map((d) => d.id);
}

export function OracleSpineTool({ onOpenTool }: ToolProps) {
  const styles = useStyles();
  const { t, c } = useTheme();
  const { session } = useAuth();
  const userId = session?.user.id;
  const [spine, setSpine] = useState<OracleSpine | null>(null);
  const [previousSpine, setPreviousSpine] = useState<OracleSpine | null>(null);
  const [selectedSector, setSelectedSector] = useState<Sector>('S');
  // Compass configuration for both ring systems (SHAPE / ORDER), shared with
  // the live ASTRA + SyQi compass (user_settings.oracle_sector_config). STAGE
  // is not a ring system — it's ORDER's chronological S/T/A/G/E layer.
  const { config, spokesFor, isEnabled, toggleBuiltin, addCustom, renameCustom, removeCustom } =
    useCompassConfig(userId);
  const [compassSystem, setCompassSystem] = useState<RingSystemId>('shape');
  const [newSectorLabel, setNewSectorLabel] = useState('');
  const enabledSectors = SECTORS.filter((s) => !config.systems.shape.disabled.includes(s));

  // If the currently-selected sector gets toggled off, jump to the first
  // enabled one so the editor never points at a hidden sector.
  useEffect(() => {
    if (config.systems.shape.disabled.includes(selectedSector)) {
      const first = SECTORS.find((s) => !config.systems.shape.disabled.includes(s));
      if (first) setSelectedSector(first);
    }
  }, [config.systems.shape.disabled, selectedSector]);
  const [showDiffPreview, setShowDiffPreview] = useState(false);
  const [diffPreviews, setDiffPreviews] = useState<SpineDiffPreview[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [approved, setApproved] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [surveyMode, setSurveyMode] = useState<{ sector: Sector } | null>(null);
  const [surveyState, setSurveyState] = useState<{
    questionIdx: number;
    leansEndeavourTallies: Record<string, number>;
  } | null>(null);

  // Load spine on mount
  useEffect(() => {
    let alive = true;
    if (!userId) return;
    (async () => {
      try {
        const loaded = await loadOracleSpine(supabase, userId);
        if (alive) {
          setSpine(loaded);
          setPreviousSpine(loaded);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading spine:', err);
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [userId]);

  const handleSetEndeavour = useCallback((sector: Sector, value: string) => {
    setSpine((s) =>
      s
        ? {
            ...s,
            [sector]: { ...s[sector], endeavour: value, endeavourSource: 'manual', isDirty: true },
          }
        : s,
    );
  }, []);

  const handleTakeSurvey = useCallback((sector: Sector) => {
    setSurveyMode({ sector });
    setSurveyState({
      questionIdx: 0,
      leansEndeavourTallies: {},
    });
  }, []);

  const handleSurveyAnswer = useCallback((endeavour: string) => {
    if (!surveyState) return;
    const updated = { ...surveyState.leansEndeavourTallies };
    updated[endeavour] = (updated[endeavour] || 0) + 1;
    setSurveyState({ ...surveyState, leansEndeavourTallies: updated, questionIdx: surveyState.questionIdx + 1 });
  }, [surveyState]);

  const handleFinishSurvey = useCallback(() => {
    if (!surveyState || !surveyMode) return;
    const tallies = surveyState.leansEndeavourTallies;
    const topEndeavour = Object.entries(tallies).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
    if (topEndeavour) {
      setSpine((s) =>
        s
          ? {
              ...s,
              [surveyMode.sector]: { ...s[surveyMode.sector], endeavour: topEndeavour, endeavourSource: 'survey', isDirty: true },
            }
          : s,
      );
    }
    setSurveyMode(null);
    setSurveyState(null);
  }, [surveyState, surveyMode]);

  const handleSetGoal = useCallback((sector: Sector, deityId: string) => {
    setSpine((s) => {
      if (!s) return s;
      const node = s[sector];
      // Changing deity: offer to re-suggest, but keep any chosen aims whose
      // label still applies under the new deity's preset list (ledger #1).
      const prevAims = node.aims || [];
      const newPresets = deityId ? getActivityPresetsForDeity(deityId) : [];
      const keptAims = deityId
        ? prevAims.filter((a) => newPresets.some((p) => p.label === a.label))
        : [];
      return {
        ...s,
        [sector]: {
          ...node,
          goal: deityId,
          aims: keptAims.length > 0 ? keptAims : null,
          aimIds: keptAims.length > 0 ? keptAims.map((a) => a.id) : null,
          isDirty: true,
        },
      };
    });
  }, []);

  const handleToggleAim = useCallback((sector: Sector, preset: OracleAim) => {
    setSpine((s) => {
      if (!s) return s;
      const node = s[sector];
      const current = node.aims || [];
      const isSelected = current.some((a) => a.id === preset.id);
      const nextAims = isSelected
        ? current.filter((a) => a.id !== preset.id)
        : [...current, preset];
      return {
        ...s,
        [sector]: {
          ...node,
          aims: nextAims.length > 0 ? nextAims : null,
          aimIds: nextAims.length > 0 ? nextAims.map((a) => a.id) : null,
          isDirty: true,
        },
      };
    });
  }, []);

  const handleShowDiffPreview = useCallback(() => {
    if (!spine || !previousSpine) return;
    const previews: SpineDiffPreview[] = [];
    for (const sector of SECTORS) {
      const preview = buildDiffPreview(
        sector,
        effectiveAims(previousSpine[sector]),
        effectiveAims(spine[sector]),
      );
      if (preview.addedAims.length > 0 || preview.removedAims.length > 0) {
        previews.push(preview);
      }
    }
    setDiffPreviews(previews);
    setShowDiffPreview(true);
  }, [spine, previousSpine]);

  const handleRePrescribe = useCallback(async () => {
    if (!userId || !spine || !previousSpine) return;
    setSaving(true);
    setApplyError(null);
    try {
      // Step 1: Save the spine state (clears isDirty)
      const updated = { ...spine };
      for (const sector of SECTORS) {
        updated[sector] = { ...updated[sector], isDirty: false };
      }
      await saveOracleSpine(supabase, userId, updated);

      // Step 2: Apply KYRS changes for each sector with diff
      for (const preview of diffPreviews) {
        const sector = preview.sector;
        const listId = await getOrCreateKyrsListForSector(supabase, userId, sector);

        // Insert new aim tasks
        if (preview.addedAims.length > 0) {
          await insertAimTasks(supabase, userId, listId, preview.addedAims, sector);
        }

        // Delist removed aim tasks
        if (preview.removedAims.length > 0) {
          await delistAimTasks(supabase, userId, listId, preview.removedAims, sector);
        }
      }

      // Update previous spine to current spine so future diffs are correct
      setPreviousSpine(updated);
      setApproved(true);
      setShowDiffPreview(false);
    } catch (err: any) {
      console.error('Error applying prescribe:', err);
      setApplyError(err.message || 'Failed to apply prescribe');
    } finally {
      setSaving(false);
    }
  }, [userId, spine, previousSpine, diffPreviews]);

  const handleOpenKyrs = useCallback(() => {
    if (onOpenTool) {
      onOpenTool('kyrs');
    }
  }, [onOpenTool]);

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={c.blueText} size="large" />
      </View>
    );
  }

  if (!spine) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>Failed to load spine</Text>
      </View>
    );
  }

  // Render survey flow if active
  if (surveyMode && surveyState) {
    return <SurveyFlow
      sector={surveyMode.sector}
      state={surveyState}
      onAnswer={handleSurveyAnswer}
      onFinish={handleFinishSurvey}
    />;
  }

  const currentNode = spine[selectedSector];
  const isDirty = currentNode.isDirty || Object.values(spine).some((n) => n.isDirty);
  // Aim stage gate: any sector with a Goal set must have at least one aim
  // selected before re-prescribing (OLMPS_V9_SPEC.md STAGE 3, ledger #1).
  const aimGateBlocking = enabledSectors.some(
    (s) => spine[s].goal && (!spine[s].aims || spine[s].aims.length === 0),
  );

  return (
    <View style={{ flex: 1 }}>
<PageBg />
<ScrollView contentContainerStyle={styles.container}>
      {/* ---- Compass config — SHAPE / ORDER. The spinning disc is the live
          ASTRA shape; toggle a spoke off to drop it (the disc re-spaces), or
          add a custom spoke. Every edit here is mirrored on the ASTRA and the
          SyQi home compass. ---- */}
      <GlassCard style={styles.shapePanel}>
        <View style={styles.systemTabs}>
          {RING_SYSTEMS.map((sys) => (
            <Pressable
              key={sys}
              onPress={() => setCompassSystem(sys)}
              style={[styles.systemTab, compassSystem === sys && styles.systemTabActive]}
            >
              <Text style={[styles.systemTabText, compassSystem === sys && styles.systemTabTextActive]}>
                {RING_SYSTEM_LABEL[sys]}
              </Text>
            </Pressable>
          ))}
        </View>

        <View style={styles.shapeBody}>
          <View style={styles.shapeLeft}>
            <SectorCompassPreview spokes={spokesFor(compassSystem)} />
          </View>

          <View style={styles.shapeRight}>
            <Text style={styles.shapeHeading}>{RING_SYSTEM_LABEL[compassSystem]} spokes</Text>
            {builtinSpokes(compassSystem).map((spoke) => (
              <View key={spoke.id} style={styles.shapeToggleRow}>
                <View style={[styles.shapeSwatch, { backgroundColor: spoke.color }]} />
                <Text style={styles.shapeRowLabel}>{spoke.label}</Text>
                <Switch
                  value={isEnabled(compassSystem, spoke.id)}
                  onValueChange={() => toggleBuiltin(compassSystem, spoke.id)}
                />
              </View>
            ))}

            {config.systems[compassSystem].custom.length > 0 ? <Text style={styles.shapeHeading}>Custom</Text> : null}
            {config.systems[compassSystem].custom.map((cs) => (
              <View key={cs.id} style={styles.shapeEditRow}>
                <View style={[styles.shapeSwatch, { backgroundColor: cs.color }]} />
                <RNTextInput
                  style={styles.shapeInput}
                  defaultValue={cs.label}
                  placeholder="Spoke name"
                  placeholderTextColor={c.slate}
                  onEndEditing={(e) => {
                    const t = e.nativeEvent.text.trim();
                    if (t && t !== cs.label) renameCustom(compassSystem, cs.id, t);
                  }}
                />
                <Pressable style={styles.shapeRemove} onPress={() => removeCustom(compassSystem, cs.id)}>
                  <Text style={styles.shapeRemoveText}>✕</Text>
                </Pressable>
              </View>
            ))}

            <Text style={styles.shapeHeading}>Add a custom spoke</Text>
            <View style={styles.shapeEditRow}>
              <RNTextInput
                style={styles.shapeInput}
                value={newSectorLabel}
                onChangeText={setNewSectorLabel}
                placeholder="e.g. Spiritual"
                placeholderTextColor={c.slate}
                returnKeyType="done"
                onSubmitEditing={() => {
                  addCustom(compassSystem, newSectorLabel);
                  setNewSectorLabel('');
                }}
              />
              <Pressable
                style={[styles.shapeAddBtn, !newSectorLabel.trim() && styles.shapeAddBtnDisabled]}
                disabled={!newSectorLabel.trim()}
                onPress={() => {
                  addCustom(compassSystem, newSectorLabel);
                  setNewSectorLabel('');
                }}
              >
                <Text style={styles.shapeAddBtnText}>+ Add</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </GlassCard>

      {/* The prescribed-future editor (Select a sector → Endeavour → Goal → Aim)
          is SHAPE-only. STAGE and ORDER are configured on their disc above and
          have no per-sector spine here (ORDER is driven by MoyRi's deities). */}
      {compassSystem === 'shape' ? (
        <>
      {/* Sector Picker — only enabled SHAPE sectors are editable here. */}
      <View style={styles.sectorPicker}>
        <Text style={styles.sectorLabel}>Select a sector</Text>
        <View style={styles.sectorButtons}>
          {enabledSectors.map((s) => (
            <Pressable
              key={s}
              onPress={() => setSelectedSector(s)}
              style={[
                styles.sectorButton,
                selectedSector === s && styles.sectorButtonActive,
              ]}
            >
              <Text
                style={[
                  styles.sectorButtonText,
                  selectedSector === s && styles.sectorButtonTextActive,
                ]}
              >
                {SECTOR_LABELS[s]}
              </Text>
            </Pressable>
          ))}
        </View>
      </View>

      {/* Spine Editor */}
      <View style={styles.editorContent}>
        {/* Endeavour Node */}
        <View style={styles.nodeSection}>
          <Text style={[styles.nodeLabel, { color: SECTOR_COLOR[selectedSector] }]}>
            {SECTOR_LABELS[selectedSector]} · 1. Endeavour
          </Text>
          <GlassCard style={styles.nodeCard}>
            {!currentNode.endeavour ? (
              <View style={styles.nodeForm}>
                <TextInput
                  placeholder="What is your endeavour for this sector?"
                  maxLength={160}
                  value=""
                  onChangeText={(text) => handleSetEndeavour(selectedSector, text)}
                  style={styles.textInput}
                  placeholderTextColor={c.slate}
                />
                <Text style={styles.presetsLabel}>Suggested presets:</Text>
                <View style={styles.presetsContainer}>
                  {PRESET_ENDEAVOURS[selectedSector].slice(0, 4).map((preset) => (
                    <Pressable
                      key={preset}
                      onPress={() => handleSetEndeavour(selectedSector, preset)}
                      style={styles.presetButton}
                    >
                      <Text style={styles.presetButtonText}>{preset}</Text>
                    </Pressable>
                  ))}
                </View>
                <Pressable style={styles.surveyButton} onPress={() => handleTakeSurvey(selectedSector)}>
                  <Text style={styles.surveyButtonText}>Or take the survey →</Text>
                </Pressable>
              </View>
            ) : (
              <View style={styles.nodeDisplay}>
                <Text style={styles.nodeValue}>{currentNode.endeavour}</Text>
                <Pressable onPress={() => handleSetEndeavour(selectedSector, '')}>
                  <Text style={styles.editLink}>Edit</Text>
                </Pressable>
              </View>
            )}
          </GlassCard>
        </View>

        {/* Goal Node (Deity Picker) */}
        {currentNode.endeavour && (
          <View style={styles.nodeSection}>
            <Text style={[styles.nodeLabel, { color: currentNode.goal ? t.ivoryFaint : t.goldBright }]}>2. Goal (Deity)</Text>
            <GlassCard style={styles.nodeCard}>
              {getSectorDeities(selectedSector).length === 0 ? (
                <Text style={styles.emptyText}>No deities available for this sector yet.</Text>
              ) : !currentNode.goal ? (
                <View style={styles.nodeForm}>
                  <Text style={styles.deityHint}>Choose a deity to pursue:</Text>
                  {getSectorDeities(selectedSector).map((deityId) => (
                    <Pressable
                      key={deityId}
                      onPress={() => handleSetGoal(selectedSector, deityId)}
                      style={styles.deityButton}
                    >
                      <Text style={styles.deityButtonText}>
                        {TOOL_NAMES[deityId] || deityId.toUpperCase()}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              ) : (
                <View style={styles.nodeDisplay}>
                  <Text style={styles.deityName}>
                    {TOOL_NAMES[currentNode.goal] || currentNode.goal.toUpperCase()}
                  </Text>
                  <Pressable onPress={() => handleSetGoal(selectedSector, '')}>
                    <Text style={styles.editLink}>Change</Text>
                  </Pressable>
                </View>
              )}
            </GlassCard>
          </View>
        )}

        {/* Aim Node */}
        {currentNode.goal && (
          <View style={styles.nodeSection}>
            <Text style={[styles.nodeLabel, { color: currentNode.aims && currentNode.aims.length > 0 ? t.ivoryFaint : t.goldBright }]}>3. Aim (Actions)</Text>
            <GlassCard style={styles.nodeCard}>
              <Text style={styles.aimHint}>
                Pick the recurring activities that consistently install{' '}
                <Text style={styles.aimHintBold}>
                  {TOOL_NAMES[currentNode.goal] || currentNode.goal.toUpperCase()}
                </Text>
                &apos;s aim — select at least one. These become tasks in{' '}
                <Text style={styles.aimHintBold}>KYRS</Text>.
              </Text>
              {getActivityPresetsForDeity(currentNode.goal).length === 0 ? (
                <Text style={styles.emptyText}>No activity presets available for this deity yet.</Text>
              ) : (
                <View style={styles.aimPresetList}>
                  {getActivityPresetsForDeity(currentNode.goal).map((preset) => {
                    const selected = (currentNode.aims || []).some((a) => a.id === preset.id);
                    return (
                      <Pressable
                        key={preset.id}
                        onPress={() => handleToggleAim(selectedSector, preset)}
                        style={[styles.aimPresetRow, selected && styles.aimPresetRowSelected]}
                      >
                        <GlassCard style={[styles.aimCheckbox, selected && styles.aimCheckboxSelected]}>
                          {selected && <Text style={styles.aimCheckboxMark}>✓</Text>}
                        </GlassCard>
                        <View style={styles.aimPresetTextCol}>
                          <Text style={styles.aimPresetLabel}>{preset.label}</Text>
                          <Text style={styles.aimPresetCadence}>{preset.cadence}</Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              )}
              {(!currentNode.aims || currentNode.aims.length === 0) && (
                <Text style={styles.aimRequiredHint}>Select at least one activity to proceed.</Text>
              )}
            </GlassCard>
          </View>
        )}
      </View>

      {/* Re-Prescribe Button + Diff Preview + Post-Apply State */}
      {(isDirty || approved) && (
        <View style={styles.actionFooter}>
          {approved ? (
            <View style={styles.successBox}>
              <View style={styles.successMessage}>
                <Text style={styles.successTitle}>Prescribed!</Text>
                <Text style={styles.successText}>KYRS tasks created/updated for affected sectors.</Text>
              </View>
              {applyError && (
                <View style={styles.errorMessage}>
                  <Text style={styles.errorTitle}>Error</Text>
                  <Text style={styles.errorText}>{applyError}</Text>
                </View>
              )}
              <Pressable
                style={[styles.openKyrsButton, saving && styles.buttonDisabled]}
                onPress={handleOpenKyrs}
                disabled={saving}
              >
                <Text style={styles.openKyrsButtonText}>Open KYRS to Schedule →</Text>
              </Pressable>
            </View>
          ) : !showDiffPreview ? (
            <View>
              <Pressable
                style={[styles.represcribeButton, (saving || aimGateBlocking) && styles.buttonDisabled]}
                onPress={handleShowDiffPreview}
                disabled={saving || aimGateBlocking}
              >
                <Text style={styles.represcribeButtonText}>Re-prescribe?</Text>
              </Pressable>
              {aimGateBlocking && (
                <Text style={styles.aimRequiredHint}>
                  Select at least one aim activity for every sector with a Goal set.
                </Text>
              )}
            </View>
          ) : (
            <GlassCard style={styles.diffPreviewBox}>
              <Text style={styles.diffPreviewTitle}>Diff Preview:</Text>
              <Text style={styles.diffPreviewLine}>
                Deity: {spine[selectedSector].goal ? TOOL_NAMES[spine[selectedSector].goal!] || spine[selectedSector].goal!.toUpperCase() : 'none'}
              </Text>
              {diffPreviews.length > 0 ? (
                <View style={styles.diffPreviewDetails}>
                  {diffPreviews.map((preview) => (
                    <View key={preview.sector} style={styles.diffSector}>
                      <Text style={styles.diffSectorName}>{SECTOR_LABELS[preview.sector]}</Text>
                      <Text style={styles.diffGroupName}>Group: {preview.kyrsGroupName}</Text>
                      {preview.addedAims.length > 0 && (
                        <View style={styles.diffAdded}>
                          <Text style={styles.diffAddedTitle}>Adding:</Text>
                          {preview.addedAims.map((aim) => (
                            <Text key={aim.id} style={styles.diffAddedItem}>
                              • {aim.label}{aim.cadence ? ` (${aim.cadence})` : ''}
                            </Text>
                          ))}
                        </View>
                      )}
                      {preview.removedAims.length > 0 && (
                        <View style={styles.diffRemoved}>
                          <Text style={styles.diffRemovedTitle}>Delisting:</Text>
                          {preview.removedAims.map((aim) => (
                            <Text key={aim.id} style={styles.diffRemovedItem}>• {aim.label}</Text>
                          ))}
                        </View>
                      )}
                    </View>
                  ))}
                </View>
              ) : (
                <Text style={styles.diffPreviewHint}>No changes to KYRS tasks.</Text>
              )}
              <View style={styles.diffActionButtons}>
                <Pressable
                  style={[styles.cancelButton, saving && styles.buttonDisabled]}
                  onPress={() => setShowDiffPreview(false)}
                  disabled={saving}
                >
                  <Text style={styles.cancelButtonText}>Cancel</Text>
                </Pressable>
                <Pressable
                  style={[styles.approveButton, saving && styles.buttonDisabled]}
                  onPress={handleRePrescribe}
                  disabled={saving}
                >
                  <Text style={styles.approveButtonText}>Approve</Text>
                </Pressable>
              </View>
            </GlassCard>
          )}
        </View>
      )}
        </>
      ) : (
        <GlassCard style={styles.systemNote}>
          <Text style={styles.systemNoteText}>
            {compassSystem === 'order'
              ? 'ORDER prongs are configured on the disc above. The O/R/D/E/R work itself now lives in MoyRi — deities, maladies, and principles.'
              : 'STAGE tiers are configured on the disc above. There is no per-tier editor here.'}
          </Text>
        </GlassCard>
      )}
    </ScrollView>
</View>
  );
}

// ---- TextInput component ----
import { TextInput as RNTextInput } from 'react-native';

function TextInput({
  placeholder,
  maxLength,
  value,
  onChangeText,
  style,
  placeholderTextColor,
}: {
  placeholder: string;
  maxLength: number;
  value: string;
  onChangeText: (text: string) => void;
  style: any;
  placeholderTextColor: string;
}) {
  return (
    <RNTextInput
      placeholder={placeholder}
      maxLength={maxLength}
      value={value}
      onChangeText={onChangeText}
      style={style}
      placeholderTextColor={placeholderTextColor}
    />
  );
}

// ---- Survey flow component ----
interface SurveyFlowProps {
  sector: Sector;
  state: { questionIdx: number; leansEndeavourTallies: Record<string, number> };
  onAnswer: (endeavour: string) => void;
  onFinish: () => void;
}

function SurveyFlow({ sector, state, onAnswer, onFinish }: SurveyFlowProps) {
  const styles = useStyles();
  const bank = SECTOR_BANKS[sector];
  const questions = bank.questions;
  const minQCount = 5;
  const maxQCount = 20;
  const questionIdx = state.questionIdx;
  const showFinishEarlyOption = questionIdx >= minQCount && questionIdx < maxQCount;
  const isComplete = questionIdx >= maxQCount;

  if (isComplete) {
    // Survey complete — show suggested endeavour
    const tallies = state.leansEndeavourTallies;
    const topEndeavour = Object.entries(tallies).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
    const topCount = tallies[topEndeavour] || 0;

    return (
      <View style={styles.surveyContainer}>
        <View style={styles.surveyContent}>
          <Text style={styles.surveyCompleteLabel}>Survey Complete</Text>
          <Text style={styles.surveyCompleteSub}>Based on your answers, we suggest:</Text>
          <GlassCard style={styles.surveyResultCard}>
            <Text style={styles.surveyResult}>{topEndeavour}</Text>
            <Text style={styles.surveyResultMeta}>({topCount} votes from {questionIdx} questions)</Text>
          </GlassCard>
          <Pressable style={styles.surveyAcceptButton} onPress={onFinish}>
            <Text style={styles.surveyAcceptButtonText}>Accept & Continue</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  const currentQuestion = questions[questionIdx];
  if (!currentQuestion) {
    return <View style={styles.surveyContainer}><Text style={styles.errorText}>No questions available</Text></View>;
  }

  return (
    <View style={{ flex: 1 }}>
<PageBg />
<ScrollView contentContainerStyle={styles.surveyContainer}>
      {/* Header */}
      <View style={styles.surveyHeader}>
        <Text style={styles.surveyHeaderLabel}>{SECTOR_LABELS[sector]} Survey</Text>
        <Text style={styles.surveyQuestionCount}>
          Question {questionIdx + 1} of {maxQCount}
        </Text>
        <View style={styles.surveyProgressBar}>
          <View
            style={[
              styles.surveyProgressFill,
              { width: `${((questionIdx + 1) / maxQCount) * 100}%` },
            ]}
          />
        </View>
      </View>

      {/* Question */}
      <View style={styles.surveyQuestion}>
        <Text style={styles.surveyQuestionText}>{currentQuestion.prompt}</Text>
        <View style={styles.surveyOptions}>
          {currentQuestion.options.map((option) => (
            <Pressable
              key={option.label}
              onPress={() => onAnswer(option.leansEndeavour)}
              style={styles.surveyOption}
            >
              <Text style={styles.surveyOptionText}>{option.label}</Text>
            </Pressable>
          ))}
        </View>
      </View>

      {/* Footer */}
      {showFinishEarlyOption && (
        <View style={styles.surveyFooter}>
          <Pressable style={styles.finishEarlyButton} onPress={onFinish}>
            <Text style={styles.finishEarlyButtonText}>Finish now (less accurate) →</Text>
          </Pressable>
        </View>
      )}
    </ScrollView>
</View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingBottom: 32,
  },
  center: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  errorText: {
    color: c.danger,
    fontSize: 13,
    textAlign: 'center',
  },
  sectorPicker: {
    marginBottom: 20,
    backgroundColor: t.glass,
    paddingBottom: 12,
    marginHorizontal: -16,
    paddingHorizontal: 16,
    ...neu.raisedSm,
  },
  sectorLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 11,
    fontWeight: '600',
    color: c.slate,
    textTransform: 'uppercase',
    letterSpacing: 0.1,
    marginBottom: 8,
  },
  sectorButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  sectorButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  sectorButtonActive: {
    backgroundColor: c.lime,
    ...neu.glowPrimary,
  },
  sectorButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: c.slate,
  },
  sectorButtonTextActive: {
    color: c.text,
  },
  shapePanel: {
    marginBottom: 20,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 14,
    gap: 10,
    ...neu.raisedSm,
  },
  systemTabs: { flexDirection: 'row', gap: 8 },
  systemTab: {
    paddingHorizontal: 16,
    paddingVertical: 7,
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  systemTabActive: { backgroundColor: c.primary, ...neu.inset },
  systemTabText: { fontSize: 12, fontWeight: '800', color: c.blueText, letterSpacing: 0.5 },
  systemTabTextActive: { color: t.onGold },
  shapeBody: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
  },
  shapeLeft: { width: 270, alignItems: 'center', justifyContent: 'center' },
  shapeRight: { flex: 1, gap: 0, minWidth: 240 },
  shapeHeading: { fontFamily: DISPLAY_FONT,
    fontSize: 11,
    fontWeight: '800',
    color: c.blueText,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 8,
    marginBottom: 3,
  },
  // Toggle rows hug their content (swatch + label + switch adjacent) so there's
  // no dead gap between the label and its switch.
  shapeToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 12,
    paddingVertical: 2,
  },
  shapeSwatch: { width: 16, height: 16, borderRadius: 5 },
  shapeRowLabel: { width: 130, fontSize: 14, fontWeight: '700', color: c.text },
  shapeEditRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 2 },
  shapeInput: {
    flex: 1,
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14,
    color: c.text,
    ...neu.insetDeep,
  },
  shapeRemove: {
    padding: 0,
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  shapeRemoveText: { color: c.danger, fontWeight: '900' },
  shapeAddBtn: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: radii.tile,
    backgroundColor: c.primary,
    ...neu.raisedSm,
  },
  shapeAddBtnDisabled: { opacity: 0.4 },
  shapeAddBtnText: { color: t.onGold, fontWeight: '800', fontSize: 13 },
  systemNote: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 16,
    ...neu.raisedSm,
  },
  systemNoteText: { fontSize: 13, color: c.slate, lineHeight: 19, fontStyle: 'italic' },
  editorContent: {
    gap: 16,
    marginBottom: 16,
  },
  nodeSection: {
    gap: 8,
  },
  nodeLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 11,
    fontWeight: '600',
    color: c.slate,
    textTransform: 'uppercase',
    letterSpacing: 0.05,
  },
  nodeCard: {
    backgroundColor: t.glass,
    borderRadius: radii.panel,
    padding: 16,
    ...neu.raisedSm,
  },
  nodeForm: {
    gap: 12,
  },
  textInput: {
    backgroundColor: c.inputFill,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    color: c.text,
    ...neu.insetDeep,
  },
  presetsLabel: {
    fontSize: 11,
    color: c.slate,
    marginBottom: 8,
  },
  presetsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 8,
  },
  presetButton: {
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    paddingHorizontal: 10,
    paddingVertical: 4,
    ...neu.raisedSm,
  },
  presetButtonText: {
    fontSize: 11,
    color: c.text,
  },
  surveyButton: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 10,
    paddingHorizontal: 12,
    alignItems: 'center',
    ...neu.raisedSm,
  },
  surveyButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: c.text,
  },
  nodeDisplay: {
    gap: 8,
  },
  nodeValue: {
    fontFamily: DISPLAY_FONT,
    fontSize: 16,
    letterSpacing: 0.5,
    color: c.text,
  },
  deityName: {
    fontFamily: DISPLAY_FONT,
    fontSize: 15,
    letterSpacing: 1,
    color: t.goldBright,
    marginBottom: 4,
  },
  editLink: {
    fontSize: 11,
    color: c.slate,
    textDecorationLine: 'underline',
  },
  deityHint: {
    fontSize: 12,
    color: c.slate,
    marginBottom: 8,
  },
  deityButton: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 10,
    ...neu.raisedSm,
  },
  deityButtonText: {
    fontSize: 14,
    color: c.text,
  },
  emptyText: {
    fontSize: 12,
    color: c.slate,
    fontStyle: 'italic',
  },
  aimHint: {
    fontSize: 12,
    color: c.slate,
    marginBottom: 12,
  },
  aimHintBold: {
    fontWeight: '600',
    color: c.text,
  },
  aimsList: {
    gap: 4,
  },
  aimItem: {
    fontSize: 12,
    color: c.text,
  },
  aimPresetList: {
    gap: 8,
  },
  aimPresetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 10,
    ...neu.raisedSm,
  },
  aimPresetRowSelected: {
    backgroundColor: c.lime,
    ...neu.glowPrimary,
  },
  aimCheckbox: {
    padding: 0,
    width: 18,
    height: 18,
    borderRadius: 4,
    backgroundColor: t.glass,
    alignItems: 'center',
    justifyContent: 'center',
    ...neu.raisedSm,
  },
  aimCheckboxSelected: {
    backgroundColor: c.primary,
    ...neu.glowPrimary,
  },
  aimCheckboxMark: {
    fontSize: 11,
    color: t.onGold,
    fontWeight: '700',
  },
  aimPresetTextCol: {
    flex: 1,
    gap: 2,
  },
  aimPresetLabel: {
    fontSize: 13,
    color: c.text,
  },
  aimPresetCadence: {
    fontSize: 11,
    color: c.slate,
  },
  aimRequiredHint: {
    fontSize: 11,
    color: t.goldBright,
    marginTop: 8,
  },
  actionFooter: {
    marginHorizontal: -16,
    marginBottom: -16,
    backgroundColor: t.glass,
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  represcribeButton: {
    backgroundColor: c.primary,
    borderRadius: radii.tile,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    ...neu.glowPrimary,
  },
  represcribeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: t.onGold,
  },
  diffPreviewBox: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 12,
    gap: 8,
    ...neu.raisedSm,
  },
  diffPreviewTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: c.text,
    marginBottom: 4,
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
  },
  diffPreviewLine: {
    fontSize: 12,
    color: c.text,
  },
  diffPreviewHint: {
    fontSize: 11,
    color: c.slate,
    marginVertical: 4,
  },
  diffActionButtons: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingVertical: 10,
    alignItems: 'center',
    ...neu.raisedSm,
  },
  cancelButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: c.text,
  },
  approveButton: {
    flex: 1,
    backgroundColor: c.lime,
    borderRadius: radii.tile,
    paddingVertical: 10,
    alignItems: 'center',
    ...neu.glowPrimary,
  },
  approveButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: c.blueText,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  // Survey flow styles
  surveyContainer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingBottom: 32,
  },
  surveyHeader: {
    marginBottom: 20,
    backgroundColor: t.glass,
    borderBottomWidth: 1,
    borderBottomColor: t.lineFaint,
    paddingBottom: 12,
    marginHorizontal: -16,
    paddingHorizontal: 16,
    paddingTop: 12,
  },
  surveyHeaderLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 11,
    fontWeight: '600',
    color: c.slate,
    textTransform: 'uppercase',
    letterSpacing: 0.1,
    marginBottom: 8,
  },
  surveyQuestionCount: {
    fontSize: 12,
    color: c.slate,
    marginBottom: 8,
  },
  surveyProgressBar: {
    height: 4,
    backgroundColor: t.track,
    borderRadius: 2,
    overflow: 'hidden',
  },
  surveyProgressFill: {
    height: '100%',
    backgroundColor: t.gold,
  },
  surveyQuestion: {
    gap: 16,
    marginBottom: 16,
  },
  surveyQuestionText: {
    fontSize: 14,
    fontWeight: '600',
    color: c.text,
  },
  surveyOptions: {
    gap: 8,
  },
  surveyOption: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 12,
    ...neu.raisedSm,
  },
  surveyOptionText: {
    fontSize: 13,
    color: c.text,
  },
  surveyFooter: {
    marginHorizontal: -16,
    marginBottom: -16,
    backgroundColor: t.glass,
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  finishEarlyButton: {
    backgroundColor: t.gold,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  finishEarlyButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: c.text,
  },
  surveyContent: {
    alignItems: 'center',
    gap: 16,
    paddingVertical: 32,
  },
  surveyCompleteLabel: { fontFamily: DISPLAY_FONT,
    fontSize: 11,
    fontWeight: '600',
    color: c.slate,
    textTransform: 'uppercase',
    letterSpacing: 0.1,
  },
  surveyCompleteSub: {
    fontSize: 13,
    color: c.text,
    textAlign: 'center',
  },
  surveyResultCard: {
    backgroundColor: t.glass,
    borderRadius: radii.panel,
    paddingHorizontal: 16,
    paddingVertical: 20,
    alignItems: 'center',
    ...neu.raisedSm,
  },
  surveyResult: {
    fontSize: 16,
    fontWeight: '600',
    color: c.text,
  },
  surveyResultMeta: {
    fontSize: 11,
    color: c.slate,
    marginTop: 8,
  },
  surveyAcceptButton: {
    backgroundColor: t.onPace,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    marginTop: 8,
  },
  surveyAcceptButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: t.onGold,
  },
  // Diff preview and success styles
  diffPreviewDetails: {
    gap: 12,
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: t.lineFaint,
  },
  diffSector: {
    borderLeftWidth: 2,
    borderLeftColor: t.gold,
    paddingLeft: 8,
    gap: 4,
  },
  diffSectorName: {
    fontSize: 12,
    fontWeight: '600',
    color: c.text,
  },
  diffGroupName: {
    fontSize: 11,
    color: c.slate,
  },
  diffAdded: {
    marginTop: 4,
    gap: 2,
  },
  diffAddedTitle: {
    fontSize: 11,
    fontWeight: '600',
    color: t.onPace,
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
  },
  diffAddedItem: {
    fontSize: 11,
    color: t.onPace,
  },
  diffRemoved: {
    marginTop: 4,
    gap: 2,
  },
  diffRemovedTitle: {
    fontSize: 11,
    fontWeight: '600',
    color: t.lapsed,
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
  },
  diffRemovedItem: {
    fontSize: 11,
    color: t.lapsed,
  },
  successBox: {
    gap: 12,
  },
  successMessage: {
    backgroundColor: c.lime,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 4,
    ...neu.glowPrimary,
  },
  successTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: c.blueText,
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
  },
  successText: {
    fontSize: 11,
    color: c.blueText,
  },
  errorMessage: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 4,
    ...neu.inset,
  },
  errorTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: c.danger,
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
  },
  openKyrsButton: {
    backgroundColor: t.gold,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  openKyrsButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: t.onGold,
  },
}),
);
